/**
 * Mock accountability data for development.
 * Includes partner, check-ins, goals, and achievements.
 */
import { subDays, subHours } from 'date-fns';
import { MOCK_CURRENT_USER_ID, MOCK_USERS } from './mock-users.data';

const now = new Date();
const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

/**
 * Active accountability partnership.
 */
export const MOCK_PARTNERSHIP = {
  id: 'mock_pair_001',
  users: [MOCK_CURRENT_USER_ID, 'mock_user_002'],
  userNames: ['Alex Rivera', 'Sarah Chen'],
  userUsernames: ['alexrivera', 'sarahchen'],
  userAvatars: [
    MOCK_USERS[0].avatarUrl,
    MOCK_USERS[1].avatarUrl,
  ],
  status: 'active',
  createdAt: subDays(now, 30).toISOString(),
  endedAt: null,
};

/**
 * Recent check-ins for the partnership.
 */
export const MOCK_CHECKINS = [
  // Today - current user
  {
    id: 'mock_ci_01',
    userId: MOCK_CURRENT_USER_ID,
    date: todayMidnight.toISOString(),
    mood: 'great',
    workedOut: true,
    ateMeal: true,
    message: 'Crushed push day today! Feeling energized.',
    createdAt: subHours(now, 2).toISOString(),
    _docSnapshot: null,
  },
  // Today - partner
  {
    id: 'mock_ci_02',
    userId: 'mock_user_002',
    date: todayMidnight.toISOString(),
    mood: 'good',
    workedOut: true,
    ateMeal: true,
    message: 'Morning yoga followed by a 5K. Great start to the day!',
    createdAt: subHours(now, 6).toISOString(),
    _docSnapshot: null,
  },
  // Yesterday - current user
  {
    id: 'mock_ci_03',
    userId: MOCK_CURRENT_USER_ID,
    date: subDays(todayMidnight, 1).toISOString(),
    mood: 'good',
    workedOut: true,
    ateMeal: true,
    message: 'Pull day was solid. Hit all my targets.',
    createdAt: subDays(now, 1).toISOString(),
    _docSnapshot: null,
  },
  // Yesterday - partner
  {
    id: 'mock_ci_04',
    userId: 'mock_user_002',
    date: subDays(todayMidnight, 1).toISOString(),
    mood: 'great',
    workedOut: true,
    ateMeal: true,
    message: 'Long run day! 10 miles at a comfortable pace.',
    createdAt: subDays(now, 1).toISOString(),
    _docSnapshot: null,
  },
  // 2 days ago
  {
    id: 'mock_ci_05',
    userId: MOCK_CURRENT_USER_ID,
    date: subDays(todayMidnight, 2).toISOString(),
    mood: 'okay',
    workedOut: true,
    ateMeal: false,
    message: 'Leg day was brutal but I showed up. Skipped meal tracking today.',
    createdAt: subDays(now, 2).toISOString(),
    _docSnapshot: null,
  },
  {
    id: 'mock_ci_06',
    userId: 'mock_user_002',
    date: subDays(todayMidnight, 2).toISOString(),
    mood: 'good',
    workedOut: true,
    ateMeal: true,
    message: 'Recovery yoga session. Listening to my body.',
    createdAt: subDays(now, 2).toISOString(),
    _docSnapshot: null,
  },
  // 3 days ago
  {
    id: 'mock_ci_07',
    userId: MOCK_CURRENT_USER_ID,
    date: subDays(todayMidnight, 3).toISOString(),
    mood: 'tough',
    workedOut: false,
    ateMeal: true,
    message: 'Rest day. Sore from yesterday but nutrition was on point.',
    createdAt: subDays(now, 3).toISOString(),
    _docSnapshot: null,
  },
  // 4 days ago
  {
    id: 'mock_ci_08',
    userId: MOCK_CURRENT_USER_ID,
    date: subDays(todayMidnight, 4).toISOString(),
    mood: 'great',
    workedOut: true,
    ateMeal: true,
    message: 'Upper body push felt amazing. New PR on overhead press!',
    createdAt: subDays(now, 4).toISOString(),
    _docSnapshot: null,
  },
  {
    id: 'mock_ci_09',
    userId: 'mock_user_002',
    date: subDays(todayMidnight, 4).toISOString(),
    mood: 'good',
    workedOut: true,
    ateMeal: true,
    message: 'Interval training at the track. Getting faster!',
    createdAt: subDays(now, 4).toISOString(),
    _docSnapshot: null,
  },
  // 5 days ago
  {
    id: 'mock_ci_10',
    userId: MOCK_CURRENT_USER_ID,
    date: subDays(todayMidnight, 5).toISOString(),
    mood: 'good',
    workedOut: true,
    ateMeal: true,
    message: 'Morning cardio. Kept it light but consistent.',
    createdAt: subDays(now, 5).toISOString(),
    _docSnapshot: null,
  },
];

/**
 * Active goals for the current user.
 */
export const MOCK_GOALS = [
  {
    id: 'mock_goal_01',
    title: 'Squat 315lbs',
    category: 'strength',
    targetValue: 315,
    currentValue: 275,
    unit: 'lbs',
    deadline: subDays(now, -30).toISOString(),
    status: 'active',
    createdAt: subDays(now, 60).toISOString(),
    completedAt: null,
  },
  {
    id: 'mock_goal_02',
    title: 'Run a 5K under 25 minutes',
    category: 'cardio',
    targetValue: 25,
    currentValue: 27.5,
    unit: 'minutes',
    deadline: subDays(now, -45).toISOString(),
    status: 'active',
    createdAt: subDays(now, 45).toISOString(),
    completedAt: null,
  },
  {
    id: 'mock_goal_03',
    title: 'Reach 180lbs body weight',
    category: 'weight',
    targetValue: 180,
    currentValue: 182,
    unit: 'lbs',
    deadline: subDays(now, -60).toISOString(),
    status: 'active',
    createdAt: subDays(now, 90).toISOString(),
    completedAt: null,
  },
  {
    id: 'mock_goal_04',
    title: 'Log meals for 30 consecutive days',
    category: 'nutrition',
    targetValue: 30,
    currentValue: 6,
    unit: 'days',
    deadline: subDays(now, -24).toISOString(),
    status: 'active',
    createdAt: subDays(now, 6).toISOString(),
    completedAt: null,
  },
];

/**
 * Completed goals.
 */
export const MOCK_COMPLETED_GOALS = [
  {
    id: 'mock_goal_05',
    title: 'Bench Press 200lbs',
    category: 'strength',
    targetValue: 200,
    currentValue: 205,
    unit: 'lbs',
    deadline: subDays(now, 5).toISOString(),
    status: 'completed',
    createdAt: subDays(now, 60).toISOString(),
    completedAt: subDays(now, 10).toISOString(),
  },
];

/**
 * Unlocked achievements for the current user.
 */
export const MOCK_ACHIEVEMENTS = [
  {
    id: 'mock_ach_01',
    type: 'first_workout',
    tier: 'bronze',
    unlockedAt: subDays(now, 240).toISOString(),
    seen: true,
  },
  {
    id: 'mock_ach_02',
    type: 'week_warrior_bronze',
    tier: 'bronze',
    unlockedAt: subDays(now, 3).toISOString(),
    seen: true,
  },
  {
    id: 'mock_ach_03',
    type: 'social_butterfly_bronze',
    tier: 'bronze',
    unlockedAt: subDays(now, 120).toISOString(),
    seen: true,
  },
  {
    id: 'mock_ach_04',
    type: 'meal_prepper',
    tier: 'bronze',
    unlockedAt: subDays(now, 45).toISOString(),
    seen: true,
  },
  {
    id: 'mock_ach_05',
    type: 'first_post',
    tier: 'bronze',
    unlockedAt: subDays(now, 238).toISOString(),
    seen: true,
  },
  {
    id: 'mock_ach_06',
    type: 'goal_getter_bronze',
    tier: 'bronze',
    unlockedAt: subDays(now, 10).toISOString(),
    seen: true,
  },
];

/**
 * Mock streak data.
 */
export const MOCK_STREAK = {
  currentStreak: 12,
  longestStreak: 28,
};
